import type {
	ApplicableDiscount,
	NoApplicableDiscountReasonCode
} from '$models/ApplicableDiscount/ApplicableDiscount';
import type { Order } from '$models/Order/Order';
import type { OrderPrices } from '$models/Order/OrderPrices';
import type { NonSubscribableProduct as WebparkingNonSubscribableProduct } from '$models/Webparking/NonSubscribableProducts';
import type { PostOrderPriceRequestData } from '$models/Webparking/OrderPrice';
import { webparking } from '$lib/webparking';

/**
 * Returns the order prices for a given order.
 *
 * @async
 * @function
 * @param {Order} order - The order to get the order prices for.
 * @param {Array<number>} allowedQuantities - The quantities that are allowed
 * @param {string} couponCodeRecurring - Coupon code to apply to recurring orders
 * @param {string} couponCodeOnetime - Coupon code to apply to onetime orders
 * @param {string} [subscriptionId] - Subscription to quote the recurring prices for
 * @returns {Promise<OrderPrice>}
 */
export async function getOrderPrices(
	order: Order,
	allowedQuantities?: Array<number> | undefined,
	couponCodeRecurring?: string | undefined | null,
	couponCodeOnetime?: string | undefined | null,
	subscriptionId?: string
): Promise<OrderPrices> {
	// Order property was used in the past, preserve backwards compatibility
	if (!couponCodeRecurring && order.couponCode) {
		couponCodeRecurring = order.couponCode;
	}
	try {
		// @TODO Allowed quantities should probably not be checked at this level
		let isAllowedQuantity = true;
		if (allowedQuantities !== undefined) {
			const quantity = order.products.reduce((accumulator, currentValue) => {
				accumulator += currentValue.quantity;
				return accumulator;
			}, 0);
			isAllowedQuantity = allowedQuantities.includes(quantity);
		}
		if (
			!isAllowedQuantity ||
			(order.products.length === 0 && order.nonSubscribableProducts.length === 0)
		) {
			return {
				productsLowestRecurringPrice: null,
				productsRecurringPrice: null,
				productsOneTimePrice: null,
				nonSubscribableProductPrices: {},
				totalNonSubscribableProductsPrice: 0,
				discountAmount: 0,
				discountAmountOnetime: 0,
				freeNonSubscribableProducts: []
			};
		}

		// Get all the applicable Webparking data concurrently
		const [
			productsLowestRecurringPrice,
			productsRecurringPrice,
			productsOneTimePrice,
			webparkingNonSubscribableProducts
		] = await Promise.all([
			getOrderPricesProductsPrice(order, 1, subscriptionId),
			getOrderPricesProductsPrice(order, order.frequency, subscriptionId),
			getOrderPricesProductsPrice(order, 0),
			getOrderPricesNonSubscribableProducts(order)
		]);

		// Construct the initial order price object
		const orderPrices: OrderPrices = {
			productsLowestRecurringPrice: productsLowestRecurringPrice,
			productsRecurringPrice: productsRecurringPrice,
			productsOneTimePrice: productsOneTimePrice,
			nonSubscribableProductPrices: getOrderPricesNonSubscribableProductPrices(
				order,
				webparkingNonSubscribableProducts
			),
			totalNonSubscribableProductsPrice: 0,
			discountAmount: 0,
			discountAmountOnetime: 0,
			freeNonSubscribableProducts: []
		};
		orderPrices.totalNonSubscribableProductsPrice = getTotalNonSubscribableProductsPrice(
			orderPrices.nonSubscribableProductPrices
		);

		// Optionally add the coupon code price data to the order prices object
		if (couponCodeRecurring || !subscriptionId) {
			const discountDataRecurring = await getOrderPricesDiscountData(
				{
					...order,
					couponCode: couponCodeRecurring || null
				},
				orderPrices
			);
			orderPrices.discountAmount = discountDataRecurring.amount;
			orderPrices.freeNonSubscribableProducts = discountDataRecurring.freeNonSubscribableProducts;
		}
		if (couponCodeOnetime || !subscriptionId) {
			const discountDataOnetime = await getOrderPricesDiscountData(
				{
					...order,
					frequency: 0,
					couponCode: couponCodeOnetime || null
				},
				orderPrices
			);
			orderPrices.discountAmountOnetime = discountDataOnetime.amount;
		}

		// Return the order prices object
		return orderPrices;

		// If any error occurs, return an OrderPrices object that indicates the order is invalid
	} catch (error) {
		// If getting the regular products prices failed, try getting just the non subscribable prices if applicable
		let nonSubscribableProductPrices = {};
		if (order && order.nonSubscribableProducts.length > 0) {
			try {
				const webparkingNonSubscribableProducts =
					await getOrderPricesNonSubscribableProducts(order);
				nonSubscribableProductPrices = getOrderPricesNonSubscribableProductPrices(
					order,
					webparkingNonSubscribableProducts
				);
			} catch (error) {}
		}

		return {
			productsLowestRecurringPrice: null,
			productsRecurringPrice: null,
			productsOneTimePrice: null,
			nonSubscribableProductPrices: nonSubscribableProductPrices,
			totalNonSubscribableProductsPrice: getTotalNonSubscribableProductsPrice(
				nonSubscribableProductPrices
			),
			discountAmount: 0,
			discountAmountOnetime: 0,
			freeNonSubscribableProducts: []
		};
	}
}

function getTotalNonSubscribableProductsPrice(nonSubscribableProductPrices) {
	return Object.values(nonSubscribableProductPrices).reduce((accumulator, currentValue) => {
		accumulator += currentValue;
		return accumulator;
	}, 0);
}

/**
 * Returns the order products price for a given order and frequency.
 *
 * @async
 * @function
 * @param {Order} order - The order to get the order price for.
 * @param {number} frequency - The frequency to get the order price for.
 * @param {string} [subscriptionId] - The subscription to get the order price for.
 * @returns {Promise<number>}
 */
export async function getOrderPricesProductsPrice(
	order: Order,
	frequency: number,
	subscriptionId?: string
): Promise<number> {
	const orderPriceRequestData: PostOrderPriceRequestData = {
		frequency
	};
	if (subscriptionId) {
		orderPriceRequestData.subscription_id = subscriptionId;
	}
	let products = [];
	if (order.products.length > 0) {
		products = order.products.filter((p) => p.quantity > 0);
		orderPriceRequestData.order_items = products;
	}
	return webparking.post('order-price', { json: orderPriceRequestData }).json();
}

/**
 * Returns the Webparking non subscribable products for a given order.
 *
 * @async
 * @function
 * @param {Order} order - The order to get the Webparking non subscribable products for.
 * @returns {Promise<WebparkingNonSubscribableProduct[]>}
 */
async function getOrderPricesNonSubscribableProducts(
	order: Order
): Promise<WebparkingNonSubscribableProduct[]> {
	if (order.nonSubscribableProducts.length === 0) {
		return Promise.resolve([]);
	}
	const json = await webparking.get(`non-subscribable-products`).json();
	return json.data;
}

/**
 * Returns the non subscribable products price for a given order and Webparking non subscribable products.
 *
 * @function
 * @param {Order} order - The order to get the non subscribable products price for.
 * @param {WebparkingNonSubscribableProduct[]} webparkingNonSubscribableProducts - The Webparking non subscribable products to get the non subscribable products price for.
 * @returns {Promise<number>}
 */
function getOrderPricesNonSubscribableProductPrices(
	order: Order,
	webparkingNonSubscribableProducts: WebparkingNonSubscribableProduct[]
): number {
	const webparkingNonSubscribableProductPricesMapping = webparkingNonSubscribableProducts.reduce(
		(accumulator, currentValue) => {
			accumulator[currentValue.id] = parseFloat(currentValue.price);
			return accumulator;
		},
		{}
	);
	return order.nonSubscribableProducts.reduce((accumulator, currentValue) => {
		if (!(currentValue.id in accumulator)) {
			accumulator[currentValue.id] =
				currentValue.quantity * webparkingNonSubscribableProductPricesMapping[currentValue.id];
		}
		return accumulator;
	}, {});
}

/**
 * Returns the products discount price for a given order and order prices.
 *
 * @async
 * @function
 * @param {Order} order - The order to get the products discount price for.
 * @param {OrderPrices} orderPrices - The order prices to get the products discount price for.
 * @returns {Promise<ApplicableDiscount>}
 */
export async function getOrderPricesDiscountData(
	order: Order,
	orderPrices: OrderPrices
): Promise<ApplicableDiscount> {
	// Prepare the applicable discount request data
	const productTotalQuantity = Object.values(order.products).reduce((accumulator, currentValue) => {
		return accumulator + currentValue.quantity;
	}, 0);
	const nonSubscribableTotalQuantity = Object.values(order.nonSubscribableProducts).reduce(
		(accumulator, currentValue) => {
			return accumulator + currentValue.quantity;
		},
		0
	);
	const productsPrice =
		order.frequency === 0 ? orderPrices.productsOneTimePrice : orderPrices.productsRecurringPrice;
	let applicableDiscounData: PostApplicableDiscountRequestData = {
		number_of_products: productTotalQuantity + nonSubscribableTotalQuantity,
		for_subscription: order.frequency > 0,
		total_product_price: productsPrice + orderPrices.totalNonSubscribableProductsPrice
	};
	if (order.couponCode) {
		applicableDiscounData.code = order.couponCode;
	}
	if (order.products.length > 0) {
		applicableDiscounData.product_ids = order.products.map((product) => product.id);
	}
	if (order.nonSubscribableProducts.length > 0) {
		applicableDiscounData.non_subscribable_product_ids = order.nonSubscribableProducts.map(
			(nonSubscribableProduct) => nonSubscribableProduct.id
		);
	}

	// TODO: Move to its own place. Should we do something with the codes?
	interface ApplicableDiscountResponseData {
		amount: number;
		free_non_subscribable_products: {
			id: string;
			name: string;
			price: number;
			quantity: number;
		}[];
		no_applicable_discount_reason: {
			code: NoApplicableDiscountReasonCode;
			message: string;
		};
		blocking_period: string | null;
		frequency: number | null;
		required_order_count?: number | null;
		maximum_order_frequency?: number | null;
	}
	// Get the applicable discount response data from the  async Webparking API call
	const applicableDiscountResponseData: ApplicableDiscountResponseData = await webparking
		.post('applicable-discount', { json: applicableDiscounData })
		.json();

	// Return the applicable discount data
	const freeNonSubscribableProductsDiscountAmount =
		applicableDiscountResponseData.free_non_subscribable_products.reduce(
			(accumulator, currentValue) => {
				let currentNonSubscribableProductQuantityInOrder = 0;
				order.nonSubscribableProducts.forEach((nonSubscribableProduct) => {
					if (currentValue.id === nonSubscribableProduct.id) {
						currentNonSubscribableProductQuantityInOrder = nonSubscribableProduct.quantity;
						return false;
					}
				});
				accumulator +=
					Math.min(currentValue.quantity, currentNonSubscribableProductQuantityInOrder) *
					currentValue.price;
				return accumulator;
			},
			0
		);

	let isApplicable: boolean =
		applicableDiscountResponseData.amount > 0 ||
		applicableDiscountResponseData.free_non_subscribable_products.length > 0;
	let noApplicableDiscountReason = applicableDiscountResponseData.no_applicable_discount_reason;

	// Handle frequency mismatch manually.
	// See: https://service.customer.webparking.nl/servicedesk/customer/portal/3/WPSD-15604
	if (
		applicableDiscountResponseData.frequency !== null &&
		applicableDiscountResponseData.frequency !== order.frequency
	) {
		isApplicable = false;
		noApplicableDiscountReason = {
			code: 'frequency-mismatch',
			message: 'Deze korting is alleen geldig voor een specifieke bezorgfrequentie.'
		};
	}

	if (
		applicableDiscountResponseData.maximum_order_frequency !== null &&
		applicableDiscountResponseData.maximum_order_frequency !== undefined &&
		order.frequency > applicableDiscountResponseData.maximum_order_frequency
	) {
		isApplicable = false;
		noApplicableDiscountReason = {
			code: 'frequency-mismatch',
			message: `Deze korting is alleen geldig voor een bezorgfrequentie van maximaal ${applicableDiscountResponseData.maximum_order_frequency} weken.`
		};
	}

	return {
		isApplicable,
		amount: isApplicable
			? applicableDiscountResponseData.amount + freeNonSubscribableProductsDiscountAmount
			: 0,
		freeNonSubscribableProducts: isApplicable
			? applicableDiscountResponseData.free_non_subscribable_products.map(
					(nonSubscribableProduct) => ({
						id: nonSubscribableProduct.id,
						name: nonSubscribableProduct.name,
						price: nonSubscribableProduct.price,
						quantity: nonSubscribableProduct.quantity
					})
				)
			: [],
		noApplicableDiscountReason,
		blockingPeriod: applicableDiscountResponseData.blocking_period,
		requiredOrderCount: applicableDiscountResponseData.required_order_count ?? null,
		maximumOrderFrequency: applicableDiscountResponseData.maximum_order_frequency ?? null
	};
}

/**
 * Automatically applies a coupon code to the next order.
 *
 * @function
 * @param {string} code
 */
export async function autoApplyDiscount(code: string) {
	await webparking.post('discount/auto-apply', {
		json: {
			code
		}
	});
}
